/**
 * Shared upload utility for DigiBox.
 *
 * Handles: file → base64, upload to coreBOS, link to contact + optional parent record.
 */

import { useCallback, useState } from "react";
import { useApiUrl } from "@refinedev/core";
import axios from "axios";
import { usePortalContact } from "./usePortalContact";

export interface UploadResult {
  id: string;
  filename: string;
  filetype: string;
  filesize: number;
}

export function useDocumentUpload() {
  const apiUrl = useApiUrl();
  const { contactId } = usePortalContact();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Convert a File object to base64 string */
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the data:...;base64, prefix
        resolve(result.split(",")[1] || result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /**
   * Upload a file and optionally link it to a parent record.
   *
   * @param file - The File object from input or drag/drop
   * @param options.description - Optional description / note content
   * @param options.linkTo - Optional parent record WSID to create M2M relation
   * @param options.category - Optional tag for the upload (e.g. "Risposta", "Documento identità")
   * @param options.fiscalDate - Optional fiscal date in YYYY-MM-DD format (Data di Competenza)
   */
  const upload = useCallback(async (
    file: File,
    options?: { description?: string; linkTo?: string; category?: string; fiscalDate?: string }
  ): Promise<UploadResult | null> => {
    setError(null);
    setUploading(true);

    try {
      const base64 = await fileToBase64(file);

      // 1. Create the Document record
      const uploadRes = await axios.post(`${apiUrl}/direct/upload-document`, {
        filename: file.name,
        filecontent: base64,
        filedescription: options?.description || options?.category || "",
        assigned_user_id: "19x1",
      });

      if (!uploadRes.data.success) {
        throw new Error(uploadRes.data.error || "Upload fallito");
      }

      const docId = uploadRes.data.result.id;

      // 1b. Set fiscal_date if provided
      if (options?.fiscalDate) {
        await axios.post(`${apiUrl}/direct/update`, {
          id: docId,
          data: { fiscal_date: options.fiscalDate },
        }).catch(() => {}); // non-blocking
      }

      // 2. Link document to the contact
      if (contactId) {
        await axios.post(`${apiUrl}/direct/set-relation`, {
          relate_this_id: contactId,
          with_these_ids: [docId],
        }).catch(() => {}); // non-blocking
      }

      // 3. Link to parent record if specified (policy, content, etc.)
      if (options?.linkTo) {
        await axios.post(`${apiUrl}/direct/set-relation`, {
          relate_this_id: options.linkTo,
          with_these_ids: [docId],
        }).catch(() => {}); // non-blocking
      }

      const result: UploadResult = {
        id: docId,
        filename: file.name,
        filetype: file.type,
        filesize: file.size,
      };

      setUploading(false);
      return result;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || "Upload fallito";
      setError(msg);
      setUploading(false);
      return null;
    }
  }, [apiUrl, contactId]);

  return { upload, uploading, error };
}
